export enum ColorNames {
  white = 'white',
  black = 'black',
  alabaster = 'alabaster',
  alto = 'alto',
  blueRibbon = 'blueRibbon',
  gallery = 'gallery',
  pearlBush = 'pearlBush',
  taupe = 'taupe',
  spicyMix = 'spicyMix',
  shadowGreen = 'shadowGreen',
  cinnabar = 'cinnabar',
}

export enum FontNames {
  menlo = 'menlo',
  bentonSansRegular = 'Benton-Sans-Regular',
  bentonSansItalic = 'Benton-Sans-Italic',
  bentonSansBlack = 'Benton-Sans-Black',
  bentonSansBlackItalic = 'Benton-Sans-Black_Italic',
  bentonSansBold = 'Benton-Sans-Bold',
  bentonSansBoldItalic = 'Benton-Sans-Bold_Italic',
  bentonSansLight = 'Benton-Sans-Light',
  bentonSansLightItalic = 'Benton-Sans-Light_Italic',
  bentonSansCondensedMedium = 'Benton-Sans-Condensed_Medium',
  bwGradualDemobold = 'BwGradualDEMO-bold',
  bwGradualMedium = 'BwGradual-Medium',
}

const validFontCategories = [
  'heading1',
  'heading2',
  'heading3',
  'body1',
  'body2',
  'body3',
  'body4',
  'mapMarkerLabel',
  'mapLabel',
  'button',
  'navigationTitle',
  'navigationLabel',
  'navigationLink',
  'chapterTitle',
  'chapterTitleLabel',
  'notification',
  'mobileNavigationLabel',
  'mapHint',
  'cardButtonLabel',
] as const;

interface ResponsiveValues {
  mobile?: number;
  tablet?: number;
  desktop?: number;
}

interface FontCategory {
  fontFamily?: FontNames | Record<string, FontNames>;
  fontSize: ResponsiveValues;
  lineHeight: ResponsiveValues;
  fontWeight: number;
  letterSpacing?: number;
  wordSpacing?: number;
  fontStretch?: 'expanded' | 'normal';
  textDecoration?: 'underline' | 'none';
  textTransform?:
    | 'none'
    | 'capitalize'
    | 'uppercase'
    | 'full-size-kana'
    | 'full-width'
    | 'lowercase';
  fontStyle?: 'normal' | 'italic' | 'oblique' | 'inherit' | 'initial' | 'unset';
}

interface Theme {
  colors: Record<ColorNames, string>;
  fonts: {
    face: Record<FontNames, string>;
    scale: Record<typeof validFontCategories[number], FontCategory>;
  };
  layout: typeof layout;
}

// "Name that Color" names
export const colors: Theme['colors'] = {
  [ColorNames.white]: '#fff',
  [ColorNames.black]: '#000',

  [ColorNames.alabaster]: '#f7f7f7',
  [ColorNames.alto]: '#ddd',
  [ColorNames.blueRibbon]: '#0070f3',
  [ColorNames.gallery]: '#eaeaea',
  [ColorNames.pearlBush]: '#e7dfd7',
  [ColorNames.taupe]: '#4a3d32',
  [ColorNames.spicyMix]: '#85513e',
  [ColorNames.shadowGreen]: '#90c6bd',
  [ColorNames.cinnabar]: '#E02E1A',
} as const;

const backupFonts = "'Helvetica Neue', Helvetica, Arial, sans-serif";

export const fonts: Theme['fonts'] = {
  face: {
    [FontNames.menlo]:
      // eslint-disable-next-line quotes, prettier/prettier
      'Menlo, Monaco, Lucida Console, Liberation Mono, DejaVu Sans Mono, Bitstream Vera Sans Mono, Courier New, monospace',
    [FontNames.bentonSansRegular]: `${FontNames.bentonSansRegular}, ${backupFonts}`,
    [FontNames.bentonSansItalic]: `${FontNames.bentonSansItalic}, ${backupFonts}`,
    [FontNames.bentonSansLight]: `${FontNames.bentonSansLight}, ${backupFonts}`,
    [FontNames.bentonSansLightItalic]: `${FontNames.bentonSansLight}, ${backupFonts}`,
    [FontNames.bentonSansBlack]: `${FontNames.bentonSansBlack}, ${backupFonts}`,
    [FontNames.bentonSansBlackItalic]: `${FontNames.bentonSansBlackItalic}, ${backupFonts}`,
    [FontNames.bentonSansBold]: `${FontNames.bentonSansBold}, ${backupFonts}`,
    [FontNames.bentonSansBoldItalic]: `${FontNames.bentonSansBoldItalic}, ${backupFonts}`,
    [FontNames.bentonSansCondensedMedium]: `${FontNames.bentonSansCondensedMedium}, ${backupFonts}`,
    [FontNames.bwGradualDemobold]: `${FontNames.bwGradualDemobold}, ${backupFonts}`,
    [FontNames.bwGradualMedium]: `${FontNames.bwGradualMedium}, ${backupFonts}`,
  },
  scale: {
    // Based on Material design typography scale, adjust as needed
    // fontSize: rem, letterSpacing: em
    // fontFamily: { [localeCode]: localeSpecificFont, default: defaultFont }
    heading1: {
      fontFamily: { default: FontNames.bwGradualMedium },
      fontSize: { desktop: 240, tablet: 126, mobile: 126 },
      lineHeight: { tablet: 1, mobile: 1 },
      fontWeight: 500,
    },
    heading2: {
      fontFamily: { default: FontNames.bwGradualMedium },
      fontSize: { desktop: 150, tablet: 90, mobile: 105 },
      lineHeight: { tablet: 1.1, mobile: 1.1 },
      fontWeight: 500,
    },
    heading3: {
      fontFamily: { default: FontNames.bwGradualMedium },
      fontSize: { desktop: 64, tablet: 46, mobile: 74 },
      lineHeight: { tablet: 1.1, mobile: 1.1 },
      fontWeight: 500,
    },
    body1: {
      fontFamily: { default: FontNames.bentonSansRegular },
      fontSize: { desktop: 48, tablet: 36, mobile: 47 },
      lineHeight: { tablet: 1.2, mobile: 1.2 },
      fontWeight: 500,
    },
    body2: {
      fontFamily: { default: FontNames.bentonSansRegular },
      fontSize: { desktop: 36, tablet: 26, mobile: 42 },
      lineHeight: { tablet: 1.3, mobile: 1.3 },
      fontWeight: 400,
    },
    body3: {
      fontFamily: { default: FontNames.bentonSansRegular },
      fontSize: { desktop: 41, tablet: 30, mobile: 47 },
      lineHeight: { tablet: 1.2, mobile: 1.2 },
      fontWeight: 400,
    },
    body4: {
      fontFamily: { default: FontNames.bentonSansRegular },
      fontSize: { desktop: 30, tablet: 30, mobile: 40 },
      lineHeight: { tablet: 1, mobile: 1 },
      fontWeight: 400,
    },
    navigationTitle: {
      fontFamily: { default: FontNames.bwGradualMedium },
      fontSize: { desktop: 88, tablet: 88, mobile: 88 },
      lineHeight: { tablet: 1.1, mobile: 1.1 },
      fontWeight: 500,
    },
    navigationLabel: {
      fontFamily: { default: FontNames.bwGradualMedium },
      fontSize: { desktop: 48, tablet: 48, mobile: 48 },
      lineHeight: { tablet: 1.1, mobile: 1.1 },
      fontWeight: 500,
    },
    navigationLink: {
      fontFamily: { default: FontNames.bentonSansRegular },
      fontSize: { desktop: 32, tablet: 28, mobile: 32 },
      lineHeight: { tablet: 1, mobile: 1 },
      fontWeight: 400,
    },
    mapMarkerLabel: {
      fontFamily: { default: FontNames.bentonSansCondensedMedium },
      fontSize: { desktop: 62, tablet: 48, mobile: 62 },
      lineHeight: { tablet: 1, mobile: 1 },
      fontWeight: 500,
      textTransform: 'uppercase',
    },
    mapLabel: {
      fontFamily: { default: FontNames.bwGradualMedium },
      fontSize: { desktop: 80, tablet: 60, mobile: 63 },
      lineHeight: { tablet: 1.1, mobile: 1.1 },
      fontWeight: 500,
    },
    chapterTitle: {
      fontFamily: { default: FontNames.bwGradualMedium },
      fontSize: { desktop: 200, tablet: 100, mobile: 126 },
      lineHeight: { tablet: 1.05, mobile: 1.05 },
      fontWeight: 500,
    },
    chapterTitleLabel: {
      fontFamily: { default: FontNames.bentonSansCondensedMedium },
      fontSize: { desktop: 32, tablet: 26, mobile: 42 },
      lineHeight: { tablet: 1.2, mobile: 1.2 },
      fontWeight: 500,
      textTransform: 'uppercase',
    },
    button: {
      fontFamily: { default: FontNames.bentonSansRegular },
      fontSize: { desktop: 31, tablet: 20, mobile: 35 },
      lineHeight: { tablet: 1, mobile: 1 },
      fontWeight: 400,
    },
    notification: {
      fontFamily: { default: FontNames.bentonSansRegular },
      fontSize: { desktop: 30, tablet: 20, mobile: 30 },
      lineHeight: { tablet: 1.3, mobile: 1.3 },
      fontWeight: 400,
    },
    mobileNavigationLabel: {
      fontFamily: { default: FontNames.bentonSansRegular },
      fontSize: { desktop: 42, tablet: 42, mobile: 42 },
      lineHeight: { tablet: 1, mobile: 1 },
      fontWeight: 500,
    },
    mapHint: {
      fontFamily: { default: FontNames.bentonSansRegular },
      fontSize: { desktop: 30, tablet: 20, mobile: 34 },
      lineHeight: { tablet: 1.3, mobile: 1.3 },
      fontWeight: 400,
    },
    cardButtonLabel: {
      fontFamily: { default: FontNames.bentonSansRegular },
      fontSize: { desktop: 30, tablet: 22, mobile: 34 },
      lineHeight: { tablet: 1, mobile: 1 },
      fontWeight: 900,
    },
  },
} as const;

// Comment unit where applicable
export const layout = {
  borderRadius: 40, // rem
  lineOpacity: 0.1,
  zIndex: {
    languageSelector: 2,
    nonFunctionals: 5,
  },
} as const;

const theme: Theme = {
  colors,
  fonts,
  layout,
} as const;

export default theme;
